// Canais.tsx
//
// Sub-tela "Canais" de Comunicações. Por loja, quais canais a rede usa para
// falar com o cliente (WhatsApp, E-mail, SMS), quem é o remetente, qual a
// credencial e quanto dá para mandar por dia.
//
// O que esta tela NÃO faz: disparar. Não existe botão de teste, de envio nem de
// "enviar agora" — nem desabilitado. O gatilho continua sendo o alerta de
// Suprimentos; aqui só se diz POR ONDE e por QUEM a mensagem sairia.
//
// Onde isso vai: acd."ActSubsidiaryInfo", subsidiaryInfoType 24, uma linha por
// filial.
//
// ARMADILHA DO NÚMERO DO TIPO — 20, 21 e 22 já estão ocupados (meios de
// pagamento, coordenada e contato) e 23 foi para o alertaConfig. `subsidiaryInfo`
// é um saco de agregados distinguidos SÓ por esse inteiro: reusar um número não
// dá erro nenhum, ele grava o agregado novo POR CIMA do antigo, em silêncio, e o
// dono descobre quando o PDV parar de receber os meios de pagamento. Por isso
// canais nasce em 24, o primeiro livre. Quem for criar o próximo agregado começa
// em 25 e atualiza este comentário.
//
// De onde as filiais vêm: do `decryptedInfoToken`, não de HTTP — mesma razão de
// MeiosPagamento.tsx (`GET /subsidiaries` não existe neste backend, e o
// controller equivalente não faz include, então voltaria sem `companyName`).

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/comunicacoesSection/index";

/** O primeiro subsidiaryInfoType livre. Ver a armadilha no cabeçalho. */
const SUBSIDIARY_INFO_CANAIS = 24;

/**
 * Nome da linha de organizationInfo onde as preferências de REDE vão morar.
 *
 * Uma constante só, usada na leitura E no envio: com o nome escrito duas vezes,
 * a tela pode passar a gravar num nome e ler de outro — e o resultado seria
 * interruptor que volta desligado depois de salvo, sem erro nenhum na tela.
 */
const ORGANIZATION_INFO_NOME_REDE = "comunicacoesRede";

type ChaveCanal = "whatsapp" | "email" | "sms";

const CANAIS: {
  chave: ChaveCanal;
  rotulo: string;
  icone: string;
  rotuloRemetente: string;
  dicaRemetente: string;
  rotuloCredencial: string;
}[] = [
  {
    chave: "whatsapp",
    rotulo: "WhatsApp",
    icone: "chat",
    rotuloRemetente: "Número remetente",
    dicaRemetente: "Só dígitos, com DDD. Ex.: 11987654321",
    rotuloCredencial: "Token da API",
  },
  {
    chave: "email",
    rotulo: "E-mail",
    icone: "email",
    rotuloRemetente: "E-mail remetente",
    dicaRemetente: "O endereço que o cliente vê como remetente",
    rotuloCredencial: "Chave/senha SMTP",
  },
  {
    chave: "sms",
    rotulo: "SMS",
    icone: "sms",
    rotuloRemetente: "Número/identificação remetente",
    dicaRemetente: "Só dígitos, com DDD. Ex.: 11987654321",
    rotuloCredencial: "Chave da operadora",
  },
];

/**
 * Os quatro interruptores que estavam em Suprimentos → Alertas e Notificações.
 * Lá eles gravavam em `formData` e chamavam `handleSave("notificationSettings")`,
 * string que o switch do shell não conhece: caía no default e nada era enviado.
 * Eles são de REDE (valem para a organização inteira), não de loja — por isso
 * vivem num bloco próprio, fora dos cartões de filial.
 */
const PREFERENCIAS_DA_REDE: {
  chave: string;
  rotulo: string;
  descricao: string;
  icone: string;
}[] = [
  {
    chave: "emailNotifications",
    rotulo: "Notificações por e-mail",
    descricao: "Alertas do sistema chegam por e-mail para quem administra",
    icone: "email",
  },
  {
    chave: "whatsappNotifications",
    rotulo: "Notificações por WhatsApp",
    descricao: "Alertas urgentes chegam no WhatsApp de quem administra",
    icone: "chat",
  },
  {
    chave: "pushNotifications",
    rotulo: "Notificações no sistema",
    descricao: "Aviso dentro da própria Central, sem sair para outro canal",
    icone: "notifications_active",
  },
  {
    chave: "autoPurchase",
    rotulo: "Pedidos automáticos ao fornecedor",
    // A descrição ANTIGA prometia "o pedido sai sozinho para o fornecedor", e
    // esse interruptor nunca teve consumidor nenhum no backend. Agora ele TEM um
    // motor por trás (a compra automática da §10.5) — e o motor tem padrão de
    // fábrica SUGERIR, com uma segunda trava no servidor. Prometer envio
    // automático aqui faria o dono acreditar que ligar a chave manda mensagem
    // para fornecedor real, e não manda.
    descricao:
      "Quando o saldo cruza o ponto de reposição, o Central monta a ordem de " +
      "compra por fornecedor e a deixa AGUARDANDO SUA APROVAÇÃO (padrão de " +
      "fábrica: sugerir). Envio automático é opt-in por SKU e ainda depende de " +
      "COMPRA_ENVIO_REAL no servidor — que está desligado.",
    icone: "shopping_cart",
  },
];

interface ConfiguracaoCanal {
  ativo: boolean;
  remetente: string;
  /** Só o que o usuário digitou AGORA. Nunca é preenchido com valor do servidor. */
  credencial: string;
  /** Marca vinda do gravado: existe credencial lá, mas ela não volta em claro. */
  credencialJaGravada: boolean;
  limiteDeEnvioPorDia: string;
}

interface ConfiguracaoLoja {
  versao: number;
  canais: Record<ChaveCanal, ConfiguracaoCanal>;
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

function canalPadrao(): ConfiguracaoCanal {
  return {
    ativo: false,
    remetente: "",
    credencial: "",
    credencialJaGravada: false,
    limiteDeEnvioPorDia: "",
  };
}

function configuracaoPadrao(): ConfiguracaoLoja {
  return {
    versao: 1,
    canais: {
      whatsapp: canalPadrao(),
      email: canalPadrao(),
      sms: canalPadrao(),
    },
  };
}

/**
 * Lê o que estiver gravado na filial. JSON corrompido não pode branquear a
 * tela: cai no padrão e o usuário vê um aviso — mesmo tratamento de
 * MeiosPagamento.tsx.
 */
function lerConfiguracao(filial: any): {
  configuracao: ConfiguracaoLoja;
  corrompida: boolean;
} {
  const linha = (filial?.subsidiaryInfo ?? []).find(
    (info: any) => Number(info?.subsidiaryInfoType) === SUBSIDIARY_INFO_CANAIS
  );
  if (!linha) return { configuracao: configuracaoPadrao(), corrompida: false };

  try {
    const bruto = JSON.parse(linha.subsidiaryInfoValue);
    const configuracao = configuracaoPadrao();
    for (const canal of CANAIS) {
      const gravado = bruto?.canais?.[canal.chave];
      configuracao.canais[canal.chave] = {
        ativo: gravado?.ativo === true,
        remetente: typeof gravado?.remetente === "string" ? gravado.remetente : "",
        // A credencial NUNCA é jogada de volta no input: mesmo que o servidor um
        // dia devolva alguma coisa aqui, reexibi-la transformaria a tela num
        // leitor de segredo para qualquer um que abrisse a Configuração.
        credencial: "",
        credencialJaGravada: Boolean(
          gravado?.credencialJaGravada || gravado?.credencial
        ),
        limiteDeEnvioPorDia:
          gravado?.limiteDeEnvioPorDia === undefined ||
          gravado?.limiteDeEnvioPorDia === null
            ? ""
            : String(gravado.limiteDeEnvioPorDia),
      };
    }
    configuracao.versao = typeof bruto?.versao === "number" ? bruto.versao : 1;
    return { configuracao, corrompida: false };
  } catch {
    return { configuracao: configuracaoPadrao(), corrompida: true };
  }
}

/** Devolve o texto do erro, ou null quando a loja pode ser enviada. */
function validar(configuracao: ConfiguracaoLoja): string | null {
  for (const canal of CANAIS) {
    const atual = configuracao.canais[canal.chave];
    if (!atual.ativo) continue;

    if (!atual.remetente.trim()) {
      return `${canal.rotulo} está ligado sem remetente. Sem remetente não há de quem a mensagem sai.`;
    }
    if (canal.chave === "email" && !atual.remetente.includes("@")) {
      return `O remetente de E-mail (“${atual.remetente}”) não parece um endereço de e-mail.`;
    }
    if (
      canal.chave !== "email" &&
      atual.remetente.replace(/\D/g, "").length < 10
    ) {
      return `O remetente de ${canal.rotulo} precisa de DDD + número (ao menos 10 dígitos).`;
    }
    if (atual.limiteDeEnvioPorDia.trim() !== "") {
      const limite = Number(atual.limiteDeEnvioPorDia);
      if (!Number.isInteger(limite) || limite < 1) {
        return `O limite diário de ${canal.rotulo} tem que ser um número inteiro maior que zero (ou vazio, para “sem limite definido”).`;
      }
    }
  }
  return null;
}

interface Props {
  decryptedInfoToken: any;
  /** A implementação real devolve Promise de { ok, tom, texto } — ver configuration/index.tsx. */
  handleSave: (objectType: any, payload?: any) => any;
}

const Canais: React.FC<Props> = ({ decryptedInfoToken, handleSave }) => {
  // Travessia organization -> companies -> subsidiaries, no padrão de Items.tsx
  // e MeiosPagamento.tsx. O token pode ser null no primeiro render: tudo com `?.`.
  const filiais: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
      }))
    );
  }, [decryptedInfoToken]);

  const [rascunhos, setRascunhos] = useState<Record<string, ConfiguracaoLoja>>({});
  const [avisos, setAvisos] = useState<Record<string, Aviso>>({});
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});
  const [preferencias, setPreferencias] = useState<Record<string, boolean>>({});
  const [avisoRede, setAvisoRede] = useState<Aviso | null>(null);
  const [salvandoRede, setSalvandoRede] = useState(false);

  // Guarda de unmount: `handleSave` é assíncrono e o usuário pode trocar de aba
  // no meio. Sem isto, o setState de depois do await cai num componente morto.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (filiais.length === 0) return;
    const iniciais: Record<string, ConfiguracaoLoja> = {};
    const avisosIniciais: Record<string, Aviso> = {};
    for (const filial of filiais) {
      const { configuracao, corrompida } = lerConfiguracao(filial);
      iniciais[filial.subsidiaryUuid] = configuracao;
      if (corrompida) {
        avisosIniciais[filial.subsidiaryUuid] = {
          tom: "atencao",
          texto:
            "O que está gravado nesta loja não é um JSON válido. A tela abriu no " +
            "padrão do sistema — salvar substituiria o valor quebrado.",
        };
      }
    }
    setRascunhos(iniciais);
    setAvisos(avisosIniciais);
  }, [filiais]);

  // As preferências de rede virão de uma linha de organizationInfo com este
  // nome, quando a gravação existir. Hoje nenhuma linha tem esse nome, então os
  // quatro abrem desligados — e é isso que a tela mostra, sem fingir estado.
  useEffect(() => {
    const linha = (
      decryptedInfoToken?.organization?.organizationInfo ?? []
    ).find(
      (info: any) => info?.organizationInfoName === ORGANIZATION_INFO_NOME_REDE
    );
    if (!linha) return;
    try {
      const bruto = JSON.parse(linha.organizationInfoValue || "{}");
      const lidas: Record<string, boolean> = {};
      for (const preferencia of PREFERENCIAS_DA_REDE) {
        lidas[preferencia.chave] = bruto?.[preferencia.chave] === true;
      }
      setPreferencias(lidas);
    } catch {
      /* valor quebrado não derruba a aba; os interruptores ficam no padrão */
    }
  }, [decryptedInfoToken]);

  const atualizarCanal = (
    subsidiaryUuid: string,
    chave: ChaveCanal,
    mudanca: (atual: ConfiguracaoCanal) => ConfiguracaoCanal
  ) => {
    setRascunhos((atuais) => {
      const configuracao = atuais[subsidiaryUuid] ?? configuracaoPadrao();
      return {
        ...atuais,
        [subsidiaryUuid]: {
          ...configuracao,
          canais: {
            ...configuracao.canais,
            [chave]: mudanca(configuracao.canais[chave]),
          },
        },
      };
    });
  };

  const salvarLoja = async (filial: any) => {
    const subsidiaryUuid = filial.subsidiaryUuid;
    const configuracao = rascunhos[subsidiaryUuid];
    if (!configuracao) return;

    // A validação acontece ANTES de qualquer chamada: erro de preenchimento é
    // problema da tela, não do servidor.
    const erro = validar(configuracao);
    if (erro) {
      setAvisos((atuais) => ({
        ...atuais,
        [subsidiaryUuid]: { tom: "erro", texto: erro },
      }));
      return;
    }

    setSalvando((atuais) => ({ ...atuais, [subsidiaryUuid]: true }));

    // O payload sai montado como a linha de subsidiaryInfo ficaria — inclusive o
    // tipo — para que ligar a rota depois seja só ligar a rota. Hoje ele não sai
    // da máquina, porque nenhuma requisição chega a ser feita.
    //
    // A CHAVE `credencial` SÓ VAI QUANDO O USUÁRIO DIGITOU ALGUMA COISA. O campo
    // abre sempre vazio (a credencial não volta do servidor em claro) e a ajuda
    // embaixo dele promete que deixar vazio MANTÉM a que está gravada. Mandar
    // `credencial: ""` junto quebraria essa promessa: quem só corrigisse o
    // limite diário apagaria a credencial boa sem tocar nela — e sem saber. Sem
    // a chave, quem for escrever a rota tem o caso "não mexer" explícito.
    // `credencialJaGravada` viaja junto para que a marca sobreviva ao round-trip
    // (é ela que decide o texto da ajuda quando a tela reabre).
    const resultado = await handleSave("canalComunicacao", {
      escopo: "loja",
      subsidiaryUuid,
      subsidiaryInfoType: SUBSIDIARY_INFO_CANAIS,
      valor: {
        versao: configuracao.versao,
        canais: Object.fromEntries(
          CANAIS.map((canal) => {
            const atual = configuracao.canais[canal.chave];
            const credencialDigitada = atual.credencial.trim() !== "";
            return [
              canal.chave,
              {
                ativo: atual.ativo,
                remetente: atual.remetente.trim(),
                ...(credencialDigitada ? { credencial: atual.credencial } : {}),
                credencialJaGravada:
                  atual.credencialJaGravada || credencialDigitada,
                limiteDeEnvioPorDia:
                  atual.limiteDeEnvioPorDia.trim() === ""
                    ? null
                    : Number(atual.limiteDeEnvioPorDia),
              },
            ];
          })
        ),
      },
    });

    if (!montado.current) return;
    setSalvando((atuais) => ({ ...atuais, [subsidiaryUuid]: false }));
    setAvisos((atuais) => ({
      ...atuais,
      [subsidiaryUuid]: resultado
        ? { tom: resultado.tom, texto: resultado.texto }
        : {
            tom: "atencao",
            texto:
              "O salvamento não respondeu nada. Nada foi confirmado — confira " +
              "antes de considerar gravado.",
          },
    }));
  };

  const salvarRede = async () => {
    setSalvandoRede(true);
    // O payload leva o NOME da linha que a leitura acima procura. Sem ele, quem
    // ligar a rota teria de adivinhar onde gravar — e adivinhar errado dá o pior
    // desfecho possível aqui: salvar sem erro e reabrir tudo desligado.
    const resultado = await handleSave("canalComunicacao", {
      escopo: "rede",
      organizationInfoName: ORGANIZATION_INFO_NOME_REDE,
      preferencias: Object.fromEntries(
        PREFERENCIAS_DA_REDE.map((preferencia) => [
          preferencia.chave,
          preferencias[preferencia.chave] === true,
        ])
      ),
    });
    if (!montado.current) return;
    setSalvandoRede(false);
    setAvisoRede(
      resultado
        ? { tom: resultado.tom, texto: resultado.texto }
        : {
            tom: "atencao",
            texto:
              "O salvamento não respondeu nada. Nada foi confirmado — confira " +
              "antes de considerar gravado.",
          }
    );
  };

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando lojas…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              forum
            </AlfredoStyles.MaterialIcons>
            Canais
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Por onde cada loja fala com o cliente. Quem dispara continua sendo o
            alerta em Suprimentos — esta tela não envia nada.
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {/* O recado vem ANTES da digitação, não depois do clique em Salvar. */}
      <AlfredoStyles.AvisoTopo $tom="atencao">
        Esta tela ainda não tem onde gravar. Os canais são um agregado de
        <strong> ActSubsidiaryInfo tipo {SUBSIDIARY_INFO_CANAIS}</strong> e o
        backend só expõe uma rota específica de meios de pagamento
        (<code>PATCH /subsidiaryInfo/:uuid/pagamento</code>) — não existe rota
        genérica por tipo. O botão Salvar chama <code>canalComunicacao</code>, o
        servidor devolve a recusa nomeada e NENHUMA requisição sai. Preencher
        aqui serve para conferir o desenho, não para configurar envio.
      </AlfredoStyles.AvisoTopo>

      {/* ---------------- PREFERÊNCIAS DA REDE ---------------- */}
      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>hub</AlfredoStyles.MaterialIcons>
          Preferências da rede
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoSubtitulo>
          Valem para a organização inteira, não para uma loja. Estavam em
          Suprimentos → Alertas e Notificações, onde chamavam
          “notificationSettings” — string sem case no salvamento, então nada era
          gravado e nada avisava.
        </AlfredoStyles.CartaoSubtitulo>

        <AlfredoStyles.Bloco>
          {PREFERENCIAS_DA_REDE.map((preferencia) => (
            <AlfredoStyles.Linha key={preferencia.chave}>
              <AlfredoStyles.Caixa
                type="checkbox"
                checked={preferencias[preferencia.chave] === true}
                onChange={(e) =>
                  setPreferencias((atuais) => ({
                    ...atuais,
                    [preferencia.chave]: e.target.checked,
                  }))
                }
              />
              <AlfredoStyles.MaterialIcons
                style={{ fontSize: "var(--fs-base)" }}
              >
                {preferencia.icone}
              </AlfredoStyles.MaterialIcons>
              <span>
                {preferencia.rotulo}
                <AlfredoStyles.AjudaCampo style={{ display: "block" }}>
                  {preferencia.descricao}
                </AlfredoStyles.AjudaCampo>
              </span>
            </AlfredoStyles.Linha>
          ))}

          {/*
            O que "Pedidos automáticos" REALMENTE faz hoje. Sem este aviso, o
            interruptor lido sozinho promete uma ação externa irreversível — e
            mandar pedido a fornecedor é exatamente isso: ele já separou, já
            carregou, já saiu.
          */}
          <AlfredoStyles.Aviso $tom="atencao">
            <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
              lock
            </AlfredoStyles.MaterialIcons>
            <span>
              <strong>Nada sai para fornecedor real.</strong> O canal de mensagem
              da compra automática roda em modo simulação: a ordem é montada, o
              texto exato fica gravado no Log de envio, e nenhum WhatsApp ou
              e-mail é disparado. O modo de fábrica é <strong>SUGERIR</strong> —
              você aprova em Suprimentos → Compras → Ordens, e existe uma janela
              de arrependimento de 15 minutos depois disso. Ligar envio real
              depende de uma variável no servidor, que não está criada.
            </span>
          </AlfredoStyles.Aviso>
        </AlfredoStyles.Bloco>

        <AlfredoStyles.AcaoRodape>
          <AlfredoStyles.Button disabled={salvandoRede} onClick={salvarRede}>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
              save
            </AlfredoStyles.MaterialIcons>
            {salvandoRede ? "Salvando…" : "Salvar preferências"}
          </AlfredoStyles.Button>
        </AlfredoStyles.AcaoRodape>

        {avisoRede && (
          <AlfredoStyles.Aviso $tom={avisoRede.tom}>
            {avisoRede.texto}
          </AlfredoStyles.Aviso>
        )}
      </AlfredoStyles.Cartao>

      {/* ---------------- UM CARTÃO POR LOJA ---------------- */}
      {filiais.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>📣</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhuma loja cadastrada
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Cadastre uma loja em “Lojas” para configurar os canais de
            comunicação dela.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        filiais.map((filial) => {
          const subsidiaryUuid = filial.subsidiaryUuid;
          const configuracao = rascunhos[subsidiaryUuid] ?? configuracaoPadrao();
          const aviso = avisos[subsidiaryUuid];

          return (
            <AlfredoStyles.Cartao key={subsidiaryUuid}>
              <AlfredoStyles.CartaoTitulo>
                <AlfredoStyles.MaterialIcons>store</AlfredoStyles.MaterialIcons>
                {filial.subsidiaryName}
              </AlfredoStyles.CartaoTitulo>
              <AlfredoStyles.CartaoSubtitulo>
                {filial.companyName}
              </AlfredoStyles.CartaoSubtitulo>

              <AlfredoStyles.BlocosGrid>
                {CANAIS.map((canal) => {
                  const atual = configuracao.canais[canal.chave];
                  return (
                    <AlfredoStyles.Bloco key={canal.chave}>
                      <AlfredoStyles.BlocoTitulo>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          {canal.icone}
                        </AlfredoStyles.MaterialIcons>
                        {canal.rotulo}
                      </AlfredoStyles.BlocoTitulo>

                      <AlfredoStyles.Linha>
                        <AlfredoStyles.Caixa
                          type="checkbox"
                          checked={atual.ativo}
                          onChange={(e) =>
                            atualizarCanal(subsidiaryUuid, canal.chave, (c) => ({
                              ...c,
                              ativo: e.target.checked,
                            }))
                          }
                        />
                        Usar {canal.rotulo} nesta loja
                      </AlfredoStyles.Linha>

                      <AlfredoStyles.Recuo $desabilitada={!atual.ativo}>
                        <AlfredoStyles.CampoGrid>
                          <AlfredoStyles.Campo>
                            <AlfredoStyles.RotuloCampo>
                              {canal.rotuloRemetente}
                            </AlfredoStyles.RotuloCampo>
                            <AlfredoStyles.Entrada
                              type="text"
                              disabled={!atual.ativo}
                              value={atual.remetente}
                              onChange={(e) =>
                                atualizarCanal(
                                  subsidiaryUuid,
                                  canal.chave,
                                  (c) => ({ ...c, remetente: e.target.value })
                                )
                              }
                            />
                            <AlfredoStyles.AjudaCampo>
                              {canal.dicaRemetente}
                            </AlfredoStyles.AjudaCampo>
                          </AlfredoStyles.Campo>

                          <AlfredoStyles.Campo>
                            <AlfredoStyles.RotuloCampo>
                              {canal.rotuloCredencial}
                            </AlfredoStyles.RotuloCampo>
                            {/* type="password" não é decoração: sem ele a
                                credencial fica legível na tela de quem passar
                                atrás do balcão. */}
                            <AlfredoStyles.Entrada
                              type="password"
                              autoComplete="new-password"
                              disabled={!atual.ativo}
                              value={atual.credencial}
                              onChange={(e) =>
                                atualizarCanal(
                                  subsidiaryUuid,
                                  canal.chave,
                                  (c) => ({ ...c, credencial: e.target.value })
                                )
                              }
                            />
                            <AjudaCredencial jaGravada={atual.credencialJaGravada} />
                          </AlfredoStyles.Campo>

                          <AlfredoStyles.Campo>
                            <AlfredoStyles.RotuloCampo>
                              Limite de envio por dia
                            </AlfredoStyles.RotuloCampo>
                            <AlfredoStyles.Entrada
                              type="number"
                              min={1}
                              step={1}
                              disabled={!atual.ativo}
                              value={atual.limiteDeEnvioPorDia}
                              onChange={(e) =>
                                atualizarCanal(
                                  subsidiaryUuid,
                                  canal.chave,
                                  (c) => ({
                                    ...c,
                                    limiteDeEnvioPorDia: e.target.value,
                                  })
                                )
                              }
                            />
                            <AlfredoStyles.AjudaCampo>
                              Quantidade de mensagens; vazio quer dizer “sem
                              limite definido”.
                            </AlfredoStyles.AjudaCampo>
                          </AlfredoStyles.Campo>
                        </AlfredoStyles.CampoGrid>
                      </AlfredoStyles.Recuo>
                    </AlfredoStyles.Bloco>
                  );
                })}
              </AlfredoStyles.BlocosGrid>

              <AlfredoStyles.AcaoRodape>
                <AlfredoStyles.Button
                  disabled={salvando[subsidiaryUuid]}
                  onClick={() => salvarLoja(filial)}
                >
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    save
                  </AlfredoStyles.MaterialIcons>
                  {salvando[subsidiaryUuid] ? "Salvando…" : "Salvar canais"}
                </AlfredoStyles.Button>
              </AlfredoStyles.AcaoRodape>

              {aviso && (
                <AlfredoStyles.Aviso $tom={aviso.tom}>
                  {aviso.texto}
                </AlfredoStyles.Aviso>
              )}
            </AlfredoStyles.Cartao>
          );
        })
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

/**
 * O texto que evita o mal-entendido mais caro deste formulário: campo de
 * credencial que reabre vazio parece campo que perdeu o valor, e o usuário
 * apaga a configuração boa achando que está consertando.
 */
const AjudaCredencial: React.FC<{ jaGravada: boolean }> = ({ jaGravada }) => (
  <AlfredoStyles.AjudaCampo>
    {jaGravada
      ? "Já existe uma credencial gravada. Ela não volta do servidor em claro: o campo abre vazio de propósito, e digitar de novo substitui a anterior."
      : "Depois de gravada, a credencial não aparece mais aqui — o campo volta vazio, e deixá-lo vazio mantém a que estiver no servidor."}
  </AlfredoStyles.AjudaCampo>
);

export default Canais;
